<template>
  <TickerPopover
    :title="detail ? $t('Schedule details') : $t('Schedules')"
    :show-back="!!detail"
    :width="detail ? 380 : 244"
    :left="left"
    :arrow-left="arrowLeft"
    @back="detail = null"
    @close="$emit('close')"
  >
    <div v-if="detail" class="schedule-popover__detail">
      <ScheduleDetail />
    </div>
    <div v-else class="schedule-popover__list">
      <ScheduleRow
        v-for="(schedule, index) in schedules"
        :key="'schedule_' + index"
        class="schedule-popover__row"
        :schedule="schedule"
        compact
        @goToDetails="openDetail(schedule)"
      />
    </div>
  </TickerPopover>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TickerPopover from '@/components/desktop/ticker-popover.vue';
import ScheduleRow from '@/components/popup/schedule-row';
import ScheduleDetail from '@/components/popup/schedule-detail.vue';

/*
 * The desktop event schedule, opened from the ticker's calendar in place of the
 * v1 schedule modal. Reuses the v1 rows and detail view; the detail opens in
 * the popover itself (with a back button) rather than in the menu modal.
 */
export default {
  name: 'SchedulePopover',
  components: {
    TickerPopover,
    ScheduleRow,
    ScheduleDetail,
  },
  props: {
    left: {
      type: Number,
      default: 0,
    },
    arrowLeft: {
      type: Number,
      default: 24,
    },
  },
  data() {
    return {
      detail: null,
    };
  },
  computed: {
    ...mapGetters('schedule', ['schedules']),
  },
  mounted() {
    this.updateSchedules();
  },
  methods: {
    ...mapActions('schedule', ['updateSchedule', 'updateSchedules']),
    openDetail(schedule) {
      // `schedule-detail.vue` reads the selected schedule from the store.
      this.updateSchedule(schedule);
      this.detail = schedule;
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * Measured from Figma node 763:3560. The popover's rows are a plain list
 * separated by hairlines, not the v1 cards: a 20px round flag, the event in the
 * gold accent, and the arena and time in white underneath. The v1 row component
 * is reused and reshaped from here, so the modal it also serves is untouched.
 */
.schedule-popover__list {
  padding: 0 12px;
  display: flex;
  flex-direction: column;
}

.schedule-popover__row {
  // 50px pitch in the design: 32px of content with 9px of air each side.
  padding: 9px 0;
  border-radius: 0;
  background: none;
  gap: 8px;

  & + & {
    border-top: 1px solid #19253c;
  }

  // The accent is carried by the event name, not by a tinted row.
  &.current {
    border-color: transparent;
    background-image: none;
  }

  ::v-deep .schedule-row__flag {
    width: 20px;
    height: 20px;
    border-radius: 50%;
  }

  ::v-deep .schedule-row__body {
    gap: 4px;
  }

  ::v-deep .schedule-row__event-name,
  ::v-deep .schedule-row__match-day {
    padding: 0;
    border-radius: 0;
    background: none;
    color: #efc87f;
    font-size: 12px;
    font-weight: 700;
    line-height: 15px;
  }

  ::v-deep .schedule-row__top {
    gap: 4px;
  }

  ::v-deep .schedule-row__meta {
    gap: 8px;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    line-height: 13px;
  }

  ::v-deep .schedule-row__desc {
    color: #fff;

    &::before {
      content: none;
    }
  }

  // Neither the fight count nor the open-in-new icon is in the design.
  ::v-deep .schedule-row__fights,
  ::v-deep .schedule-row__link {
    display: none;
  }
}

.schedule-popover__detail {
  padding: 12px 12px 16px;
}
</style>
